import {isEqual, isUndefined, noop, throttle} from 'lodash';
import React, {memo, useEffect, useState} from 'react';
import styled from 'styled-components';

import {FrontWindow} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {isDesktopAppVersionGTE} from '../../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {useLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {
  ElectronPoint,
  setMacOsTrafficLightPositionAsync,
} from '../../../services/electron/electronBridgeOutbound';
import {
  getAreMacOsTrafficLightsVisible,
  getCanSetMacOsTrafficLightPosition,
  getZoomFactor,
} from '../../../store/electron/electronSelectors';

/*
 * Props.
 */

interface MacOsTrafficLightsProps {
  className?: string;
  position: ElectronPoint;
}

/*
 * Constants.
 */

// The size of the traffic lights, not taking into account device scale (retina) or browser zoom.
const logicalWidth = 52;
const logicalHeight = 12;

/*
 * Style.
 */

interface PlaceholderStyleProps {
  $zoomRatio: number;
}

/** Reserves space for the macOS traffic lights. */
const PlaceholderDiv = styled.div<PlaceholderStyleProps>`
  flex: none;
  // The native controls don't zoom with the web app.
  width: ${(p) => Math.round(logicalWidth / p.$zoomRatio)}px;
`;

/*
 * Component.
 */

// Memoize with a deep equality check to avoid making expensive frivolous calls to Electron.
export const MacOsTrafficLights = memo<MacOsTrafficLightsProps>(({className, position}) => {
  const areTrafficLightsVisible = useAppSelector(getAreMacOsTrafficLightsVisible);
  const canSetPosition = useAppSelector(getCanSetMacOsTrafficLightPosition);
  const electronZoomRatio = useAppSelector(getZoomFactor);

  const {window} = useLayer();

  // TODO: Deprecate when most desktop app builds in the wild expose the zoom factor directly.
  const fallbackZoomRatio = useZoomRatio(window, areTrafficLightsVisible && isUndefined(electronZoomRatio));

  const zoomRatio = electronZoomRatio ?? fallbackZoomRatio;

  useEffect(() => {
    if (!canSetPosition) {
      return;
    }

    const {x, y} = position;

    // Keep the traffic lights left-aligned to web app elements when it is zoomed.
    const zoomedX = x * zoomRatio;

    // Keep the traffic lights vertically centered when the web app is zoomed.
    const yCenterOffset = logicalHeight / 2;
    let zoomedY = (y + yCenterOffset) * zoomRatio - yCenterOffset;

    // Before Electron 13, the traffic light y-position had a bug requiring it to be adjusted.
    // This can be removed when Front 3.28 is EOL'd.
    if (!isDesktopAppVersionGTE('3.29.0')) {
      zoomedY *= 2;
    }

    setMacOsTrafficLightPositionAsync({
      x: zoomedX,
      y: zoomedY,
    });
  }, [position, window, zoomRatio, canSetPosition]);

  if (!areTrafficLightsVisible) {
    return null;
  }

  return <PlaceholderDiv className={className} $zoomRatio={zoomRatio} />;
}, isEqual);

/*
 * Hooks.
 */

function useZoomRatio(window: FrontWindow, isEnabled = true): number {
  const [zoomRatio, setZoomRatio] = useState(computeZoomRatio(window));

  useEffect(() => {
    if (!isEnabled) {
      return noop;
    }

    // When zooming, use the leading edge to emit the new zoom ratio immediately. When resizing, the innerWidth and
    // outerWidth may change at different rates, so we use the trailing edge to emit once things settle.
    const listener = throttle(() => setZoomRatio(computeZoomRatio(window)), 250, {
      leading: true,
      trailing: true,
    });
    window.addEventListener('resize', listener);

    return () => {
      window.removeEventListener('resize', listener);
      listener.cancel();
    };
  }, [window, isEnabled]);

  return zoomRatio;
}

/*
 * Helpers.
 */

// Caution: this makes many assumptions and only works correctly in the Front desktop app.
function computeZoomRatio(window: FrontWindow): number {
  return window.outerWidth / window.innerWidth;
}
